import { ReactElement, useMemo, useState } from 'react';
import { Modal, PrimaryButton, SecondaryButton, Typography } from '@weareopenr/component-library';
import ModalImg from 'src/assets/images/section-modal.png';
import { ButtonContainer } from 'src/components/ButtonContainer/ButtonContainer';
import { MenuSetMenusStep } from 'src/components/CreateMenuSetModal/MenuSetMenusStep';
import { IMenuSetDetail } from 'src/api/getMenuSet';
import { EntitySource } from 'src/constants';
import { useMenus } from 'src/hooks/useMenus';
import { getCompatibleMenus } from 'src/utils/menuSets/getCompatibleMenus';
import styles from './AddMenusToMenuSetModal.module.scss';

interface IAddMenusToMenuSetModalProps {
  open: boolean;
  menuSet: IMenuSetDetail;
  closeModal: () => void;
  onAddMenus: (menuIds: string[]) => void;
  isSubmitting?: boolean;
}

const AddMenusToMenuSetContent = ({
  menuSet,
  closeModal,
  onAddMenus,
  isSubmitting,
}: Omit<IAddMenusToMenuSetModalProps, 'open'>): ReactElement => {
  const { data: menus, isLoading } = useMenus();
  const [selectedMenuIds, setSelectedMenuIds] = useState<string[]>([]);

  const availableMenus = useMemo(() => {
    const menuIdsInSet = new Set(menuSet.menus.map(({ menu_id }) => menu_id));
    const source = menuSet.ranges.length > 0 ? EntitySource.RANGES : EntitySource.LIBRARY;
    const rangeIds = menuSet.ranges.map(({ range_id }) => range_id);
    return getCompatibleMenus(menus ?? [], source, rangeIds).filter(({ menuId }) => !menuIdsInSet.has(menuId));
  }, [menus, menuSet]);

  return (
    <div className={styles['content']}>
      <Typography size="xxlarge" className={styles['title']}>
        Add menu(s) to <strong>{menuSet.name}</strong>
      </Typography>

      <div className={styles['list']}>
        <MenuSetMenusStep
          menus={availableMenus}
          selectedMenuIds={selectedMenuIds}
          onMenusChange={setSelectedMenuIds}
          isLoading={isLoading}
        />
      </div>

      <div className={styles['footer']}>
        <ButtonContainer
          margin="none"
          left={
            <SecondaryButton buttonStyle="outline" type="button" size="full-width" onClick={closeModal}>
              Back
            </SecondaryButton>
          }
          right={
            <PrimaryButton
              type="button"
              size="full-width"
              loading={isSubmitting}
              disabled={selectedMenuIds.length === 0 || isSubmitting}
              onClick={() => onAddMenus(selectedMenuIds)}
            >
              Add Menu(s)
            </PrimaryButton>
          }
        />
      </div>
    </div>
  );
};

export const AddMenusToMenuSetModal = ({ open, closeModal, ...props }: IAddMenusToMenuSetModalProps): ReactElement => (
  <Modal
    open={open}
    closeModal={closeModal}
    image={ModalImg}
    imageAlt="Picture of four beautifully displayed breakfast plates"
  >
    <AddMenusToMenuSetContent closeModal={closeModal} {...props} />
  </Modal>
);

export default AddMenusToMenuSetModal;
